import React from 'react';
import {
  BooleanInput,
  NumberInput,
  SimpleForm,
  SimpleFormProps,
  TextInput,
  required,
  useRecordContext,
  SaveButton,
  Toolbar,
  DeleteButton,
  useRedirect,
  useResourceContext,
} from 'react-admin';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';

import { TargetListAutocompleteInput } from './shared';
import {
  DateInputGroup,
  FormDivider,
  FormHeader,
  FormSectionHeader,
} from './form-components';
import { ToolbarDuplicateButton } from '../buttons/toolbar-duplicate-button';

interface LeadGenCampaignFormProps extends Partial<SimpleFormProps> {
  type: 'Create' | 'Update';
}

const LeadGenCampaignToolbar = ({ isEdit }: { isEdit: boolean }) => {
  const redirect = useRedirect();
  const resource = useResourceContext();

  const handleCancel = () => {
    redirect('list', resource);
  };

  const onDeleteSuccess = () => {
    redirect('list', resource);
  };

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Button
        variant="text"
        color="inherit"
        startIcon={<CloseIcon />}
        onClick={handleCancel}
      >
        Cancel
      </Button>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {isEdit && <ToolbarDuplicateButton />}
        <SaveButton />
        {isEdit && (
          <DeleteButton
            mutationMode="pessimistic"
            mutationOptions={{ onSuccess: onDeleteSuccess }}
          />
        )}
      </Box>
    </Toolbar>
  );
};

export const LeadGenCampaignForm: React.FC<LeadGenCampaignFormProps> = ({ type, ...rest }) => {
  const record = useRecordContext();

  // Use targetListIdsList directly from the record (already an array of IDs)
  const targetListIdsList = record?.targetListIdsList || [];

  // Default dates: now to 1 year from now
  const now = new Date();
  const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());

  const defaultValues = {
    ...rest.defaultValues,
    isApproved: false,
    priority: 0,
    maxViewsPerNpi: 1,
    maxViewsPerSession: 1,
    startsAt: now,
    endsAt: oneYearFromNow,
  };

  // For edit mode, override with actual values from record
  const formValues = type === 'Update' && record ? {
    ...record,
    targetListIdsList,
  } : defaultValues;

  const validateTargeting = (value: any, allValues: any) => {
    if (allValues.isApproved && (!value || value.length === 0)) {
      return 'At least one target list is required when campaign is approved';
    }
    return undefined;
  };

  return (
    <SimpleForm
      {...rest}
      defaultValues={formValues}
      toolbar={<LeadGenCampaignToolbar isEdit={type === 'Update'} />}
      mode="onBlur"
      reValidateMode="onBlur"
    >
      <FormHeader>{`${type} Lead Gen Campaign`}</FormHeader>

      <FormSectionHeader>Campaign Details</FormSectionHeader>
      <TextInput
        source="name"
        label="Campaign Name (Internal)"
        fullWidth
        resettable
        multiline
        validate={required()}
      />
      <BooleanInput source="isApproved" label="Approved" defaultValue={false} />

      <FormDivider />

      <FormSectionHeader>Targeting Configuration</FormSectionHeader>
      <DateInputGroup minStartsAt={now} maxEndsAt={oneYearFromNow} />
      <Box marginTop={2}>
        <TargetListAutocompleteInput
          source="targetListIdsList"
          validate={validateTargeting}
        />
      </Box>

      <FormDivider />

      <FormSectionHeader>Frequency & Priority</FormSectionHeader>
      <NumberInput
        source="maxViewsPerNpi"
        label="Max Views Per NPI"
        defaultValue={1}
        min={1}
        max={10}
        helperText="Maximum times a single NPI can see this campaign"
      />
      <NumberInput
        source="maxViewsPerSession"
        label="Max Views Per Session"
        defaultValue={1}
        min={1}
        max={5}
        helperText="Maximum times shown in a single session"
      />
      <NumberInput
        source="priority"
        label="Campaign Priority"
        defaultValue={0}
        min={0}
        max={100}
        helperText="Higher priority campaigns are shown first (0-100)"
      />
    </SimpleForm>
  );
};
